import React from 'react';
import { elementStyleFrom } from '../Helpers/searchPageConfig';
import {
    INotificationBar, isDismissed, isScheduledNow, liveBars, parseNotificationsConfig, recordDismissal,
} from '../Helpers/notificationsConfig';

// The stack of notification bars that sits above the header. Rendered INSIDE the header stack
// wrapper (see NavBar), which is what makes "bumps the header down" work with no layout maths of its
// own: the wrapper already publishes its measured height as --sf-navbar-height through a
// ResizeObserver, and #content offsets by that (index.css). Adding a bar grows the wrapper and the
// page slides down; dismissing one shrinks it and the page slides back up.
//
// Bars are evaluated against a clock read once per mount rather than a live ticker. A bar whose
// window opens or closes mid-visit changes on the next navigation or reload, which is the right
// trade for a merchandising banner — a timer per bar would repaint the whole header for a boundary
// almost nobody is sitting on.

interface INotificationBarsProps {
    /** Raw settings.notificationsConfig. Absent/empty/malformed renders nothing. */
    config?: string;
}

const NotificationBars: React.FC<INotificationBarsProps> = ({ config }) => {
    const parsed = React.useMemo(() => parseNotificationsConfig(config), [config]);
    // One clock for the whole stack, captured on mount so every bar is judged against the same
    // instant and a re-render can't reshuffle which ones are live.
    const now = React.useMemo(() => Date.now(), []);

    // Bars closed during THIS page view. Kept separately from the persisted record so the close is
    // instant and doesn't depend on a storage write that may be blocked.
    const [closed, setClosed] = React.useState<string[]>([]);

    const scheduled = React.useMemo(() => liveBars(parsed, now), [parsed, now]);
    // isDismissed reads storage, so it stays out of the memo above — that one is pure config+clock.
    const visible = scheduled.filter((b) => !closed.includes(b.id) && !isDismissed(b));

    if (visible.length === 0) return null;

    const dismiss = (bar: INotificationBar) => {
        recordDismissal(bar);
        setClosed((prev) => (prev.includes(bar.id) ? prev : [...prev, bar.id]));
    };

    return (
        <div className='sf-notification-bars'>
            {visible.map((bar) => {
                const dismissible = bar.dismissMode !== 'none';
                return (
                    <div
                        key={bar.id}
                        className='sf-notification-bar'
                        role='status'
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.75em',
                            // Keeps the text clear of the close button on narrow screens, where the
                            // button overlays the right edge rather than taking a column of its own.
                            padding: dismissible ? '0.6em 2.75em' : '0.6em 1em',
                            boxSizing: 'border-box',
                            position: 'relative',
                            textAlign: 'center',
                            backgroundColor: '#0f6cbd',
                            color: '#fff',
                            fontSize: '0.9rem',
                            lineHeight: 1.35,
                            ...elementStyleFrom(bar.bar),
                        }}
                    >
                        <span style={{ ...elementStyleFrom(bar.message) }}>{bar.text}</span>
                        {!!bar.linkText && !!bar.linkUrl && (
                            <a
                                href={bar.linkUrl}
                                target={bar.newTab ? '_blank' : undefined}
                                rel={bar.newTab ? 'noopener noreferrer' : undefined}
                                style={{
                                    // Inherits the bar's color by default so a CTA on a dark band stays
                                    // readable without the merchant having to restate the color.
                                    color: 'inherit',
                                    textDecoration: 'underline',
                                    whiteSpace: 'nowrap',
                                    ...elementStyleFrom(bar.link),
                                }}
                            >
                                {bar.linkText}
                            </a>
                        )}
                        {dismissible && (
                            <button
                                type='button'
                                aria-label='Dismiss notification'
                                onClick={() => dismiss(bar)}
                                style={{
                                    position: 'absolute',
                                    right: '0.5em',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    color: 'inherit',
                                    cursor: 'pointer',
                                    fontSize: '1.1em',
                                    lineHeight: 1,
                                    padding: '0.25em 0.4em',
                                    ...elementStyleFrom(bar.closeButton),
                                }}
                            >
                                ✕
                            </button>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

/**
 * Would this config put anything on screen right now? NavBar asks BEFORE rendering, because the
 * notification stack is what decides whether the header needs its wrapper at all — and a store with
 * no bars (or none live) has to keep rendering the bare <nav> it always did, byte for byte.
 */
export function hasVisibleNotificationBars(config?: string): boolean {
    const parsed = parseNotificationsConfig(config);
    if (!parsed.bars?.length) return false;
    const now = Date.now();
    return parsed.bars.some((b) => !b.disabled && isScheduledNow(b, now) && !isDismissed(b));
}

export default NotificationBars;
